import { useState, useMemo, useCallback, useEffect } from 'react';
import Papa from 'papaparse';
import { useQueryClient } from 'react-query';
import useApiFetch from '@/hooks/useApiFetch';
import useCategorizationLearning from '@/hooks/useCategorizationLearning';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeAiService from '@/services/financeAi';
import financeBankAccountsService from '@/services/financeBankAccounts';
import financeCategoriesService from '@/services/financeCategories';
import financeCategorizationEventsService from '@/services/financeCategorizationEvents';
import financeImportsService from '@/services/financeImports';
import financeRulesService from '@/services/financeRules';
import financeSubscriptionsService from '@/services/financeSubscriptions';
import financeTransactionsService from '@/services/financeTransactions';
import financeVendorsService from '@/services/financeVendors';
import { FetchApiIds } from '@/types';
import {
  FxVendor,
  FxCategory,
  FxCategorizationRule,
  FxImportReviewRow,
  FxSubscription,
  FxBankAccount,
} from '@/types/api/finance';
import FinanceXImportComponent from './FinanceXImport.component';

export type ImportStep = 'upload' | 'mapping' | 'processing' | 'review' | 'done';

export type ColumnMapping = {
  date: string;
  description: string;
  amount: string;
  debit: string;
  credit: string;
  cardMember: string;
};

// Patterns that indicate a payment/credit row (not a real transaction)
const PAYMENT_PATTERNS = [
  /payment.*thank\s*you/i,
  /^autopay\s+payment/i,
  /^mobile\s+payment/i,
  /wireless\s+credit/i,
];

function isPaymentRow(description: string): boolean {
  return PAYMENT_PATTERNS.some((p) => p.test(description));
}

/**
 * Clean bank description by stripping trailing city/state/country patterns.
 * e.g. "CLAUDE.AI SUBSCRIPTISAN FRANCISCO       CA" → "CLAUDE.AI SUBSCRIPTION"
 */
function cleanDescription(raw: string): string {
  let desc = raw.trim();
  // Remove email-like suffixes: _XXXX@DOMAIN.COM or standalone EMAIL@DOMAIN.COM
  desc = desc.replace(/_[A-Z0-9]+@[\w.]+\s*$/i, '');
  desc = desc.replace(/\s+[A-Z0-9._%+-]+@[\w.]+\s*$/i, '');
  // Remove trailing 2-letter country/state codes (e.g. "CA", "NY", "NL", "GB", "SG", "AU")
  desc = desc.replace(/\s{2,}[A-Z]{2}$/, '');
  // Remove trailing city name (multiple spaces before city)
  desc = desc.replace(/\s{2,}[\w\s./-]+$/, '');
  // Remove common suffixes like "888-294-6804" (phone numbers)
  desc = desc.replace(/\s+\d{3}[-.]?\d{3}[-.]?\d{4}\s*$/, '');
  // Remove trailing URLs fragments
  desc = desc.replace(/\s+(AMZN\.COM\/BILL|G\.CO\/HELPPAY#|AWS\.Amazon\.com)\s*$/i, '');
  // Normalize "VENDOR *PRODUCT" → "VENDOR PRODUCT" (Google, Apple, etc.)
  desc = desc.replace(/\s*\*\s*/, ' ');
  // Clean up any remaining trailing whitespace
  return desc.trim() || raw.trim();
}

// Date pattern validation for auto-mapping
const DATE_PATTERNS = [
  /^\d{1,2}\/\d{1,2}\/\d{2,4}$/, // MM/DD/YYYY or M/D/YY
  /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
  /^\d{1,2}-\d{1,2}-\d{2,4}$/, // MM-DD-YYYY
  /^\d{1,2}\.\d{1,2}\.\d{2,4}$/, // MM.DD.YYYY
];

function looksLikeDate(value: string): boolean {
  const trimmed = value.trim();
  return DATE_PATTERNS.some((p) => p.test(trimmed));
}

function looksLikeAmount(value: string): boolean {
  const cleaned = value.trim().replace(/[$,]/g, '');
  return cleaned !== '' && !Number.isNaN(parseFloat(cleaned)) && Number.isFinite(Number(cleaned));
}

function looksLikeDescription(value: string): boolean {
  return value.trim().length > 0;
}

function applyRules(
  description: string,
  rules: FxCategorizationRule[],
  transferCategoryIds?: Set<string>
): {
  vendorId: string | null;
  categoryId: string | null;
  ruleId: string;
  isTransfer: boolean;
} | null {
  const lower = description.toLowerCase();
  for (const rule of rules) {
    let matched = false;
    switch (rule.matchType) {
      case 'exact':
        matched = lower === rule.matchPattern.toLowerCase();
        break;
      case 'contains':
        matched = lower.includes(rule.matchPattern.toLowerCase());
        break;
      case 'regex':
        try {
          matched = new RegExp(rule.matchPattern, 'i').test(description);
        } catch {
          matched = false;
        }
        break;
      default:
        break;
    }
    if (matched) {
      const isTransfer = !!(rule.categoryId && transferCategoryIds?.has(rule.categoryId));
      return { vendorId: rule.vendorId, categoryId: rule.categoryId, ruleId: rule.id, isTransfer };
    }
  }
  return null;
}

/**
 * Build a duplicate detection key from a transaction.
 * Uses (date, lowercase cleaned description, amount) triple.
 */
function duplicateKey(date: string, description: string, amount: number): string {
  return `${date}|${description.toLowerCase().replace(/\s+/g, ' ').trim()}|${amount}`;
}

const FinanceXImportContainer = function (): JSX.Element {
  const queryClient = useQueryClient();
  const orgId = getActiveOrgId();
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedRows, setParsedRows] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    date: '',
    description: '',
    amount: '',
    debit: '',
    credit: '',
    cardMember: '',
  });
  const [reviewRows, setReviewRows] = useState<FxImportReviewRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [sourceAccount, setSourceAccount] = useState('');
  const [, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoMapped, setAutoMapped] = useState(false);
  const [isLearning, setIsLearning] = useState(false);
  const [invertSign, setInvertSign] = useState(false);

  const vendorsResponse = useApiFetch<{ data: FxVendor[] }, void>(
    [FetchApiIds.FX_VENDORS, orgId],
    async () => financeVendorsService.getAll({ page: 1, take: 200 }),
    { retry: false }
  );

  const categoriesResponse = useApiFetch<FxCategory[], void>(
    [FetchApiIds.FX_CATEGORIES, orgId],
    async () => financeCategoriesService.getAll(),
    { retry: false }
  );

  const rulesResponse = useApiFetch<FxCategorizationRule[], void>(
    ['fx-active-rules', orgId],
    async () => financeRulesService.getAllActive(),
    { retry: false }
  );

  const bankAccountsResponse = useApiFetch<FxBankAccount[], void>(
    ['fx-bank-accounts', orgId],
    async () => financeBankAccountsService.getAll(),
    { retry: false }
  );

  const vendors = useMemo(() => (vendorsResponse.data as any)?.data ?? [], [vendorsResponse.data]);

  const categories = useMemo(
    () => (categoriesResponse.data as FxCategory[]) ?? [],
    [categoriesResponse.data]
  );

  const rules = useMemo(
    () => (rulesResponse.data as FxCategorizationRule[]) ?? [],
    [rulesResponse.data]
  );

  const bankAccounts = useMemo(
    () => (bankAccountsResponse.data as FxBankAccount[]) ?? [],
    [bankAccountsResponse.data]
  );

  const { learnInBackground } = useCategorizationLearning({ vendors, categories, rules });

  // Phase 4D: Compute vendor confidence overrides from existing transaction data
  const vendorConfidenceMap = useMemo(() => {
    // This is a lightweight in-memory map; in future could be persisted
    return new Map<string, { accepted: number; overridden: number }>();
  }, []);

  const getAutoAcceptThreshold = useCallback(
    (vendorId: string | null): number => {
      if (!vendorId) return 0.8;
      const stats = vendorConfidenceMap.get(vendorId);
      if (!stats || stats.accepted + stats.overridden < 5) return 0.8;
      const overrideRate = stats.overridden / (stats.accepted + stats.overridden);
      return overrideRate > 0.3 ? 0.5 : 0.8;
    },
    [vendorConfidenceMap]
  );

  const handleFileUpload = useCallback((file: File) => {
    setFileName(file.name);
    setError(null);
    setAutoMapped(false);
    Papa.parse(file, {
      complete: (result) => {
        const data = result.data as string[][];
        if (data.length < 2) {
          setError('CSV file must have at least a header row and one data row.');
          return;
        }
        setHeaders(data[0]);
        const filteredRows = data.slice(1).filter((row) => row.some((cell) => cell.trim()));
        setParsedRows(filteredRows);

        // Auto-detect columns
        const headerLower = data[0].map((h) => h.toLowerCase().trim());
        const dateCol = data[0][headerLower.findIndex((h) => h.includes('date'))] ?? '';
        const descCol =
          data[0][
            headerLower.findIndex(
              (h) => h.includes('description') || h.includes('memo') || h.includes('narrative')
            )
          ] ?? '';
        const amountCol =
          data[0][headerLower.findIndex((h) => h === 'amount' || h.includes('amount'))] ?? '';
        const debitCol =
          data[0][
            headerLower.findIndex(
              (h) => h === 'debit' || h.includes('debit') || h.includes('withdrawal')
            )
          ] ?? '';
        const creditCol =
          data[0][
            headerLower.findIndex(
              (h) => h === 'credit' || h.includes('credit') || h.includes('deposit')
            )
          ] ?? '';
        const cardMemberCol =
          data[0][
            headerLower.findIndex(
              (h) =>
                h.includes('card member') ||
                h.includes('cardholder') ||
                h.includes('member') ||
                h.includes('user')
            )
          ] ?? '';

        // Auto-detect source account from Account # column
        const accountIdx = headerLower.findIndex((h) => h.includes('account'));
        if (accountIdx !== -1) {
          const firstAccount = data[1]?.[accountIdx]?.trim();
          if (firstAccount) {
            setSourceAccount(`Card ${firstAccount}`);
          }
        }

        const mapping: ColumnMapping = {
          date: dateCol,
          description: descCol,
          amount: amountCol,
          debit: debitCol,
          credit: creditCol,
          cardMember: cardMemberCol,
        };
        setColumnMapping(mapping);

        // Phase 1: Validate auto-detected columns against first data row
        const hasAmountCol = amountCol || (debitCol && creditCol);
        if (dateCol && descCol && hasAmountCol && filteredRows.length > 0) {
          const dateIdx = data[0].indexOf(dateCol);
          const descIdx = data[0].indexOf(descCol);
          const amountIdx = amountCol ? data[0].indexOf(amountCol) : -1;
          const debitIdx = debitCol ? data[0].indexOf(debitCol) : -1;

          // Find first non-empty value for each column
          const firstDate = filteredRows.find((r) => r[dateIdx]?.trim())?.[dateIdx] ?? '';
          const firstAmountVal =
            amountIdx !== -1
              ? filteredRows.find((r) => r[amountIdx]?.trim())?.[amountIdx] ?? ''
              : filteredRows.find((r) => r[debitIdx]?.trim())?.[debitIdx] ?? '';
          const firstDesc = filteredRows.find((r) => r[descIdx]?.trim())?.[descIdx] ?? '';

          if (
            looksLikeDate(firstDate) &&
            looksLikeAmount(firstAmountVal) &&
            looksLikeDescription(firstDesc)
          ) {
            setAutoMapped(true);
          }
        }

        setStep('mapping');
      },
      error: () => {
        setError('Failed to parse CSV file.');
      },
    });
  }, []);

  const handleStartProcessing = useCallback(async () => {
    setStep('processing');
    setIsProcessing(true);
    setError(null);

    try {
      const dateIdx = headers.indexOf(columnMapping.date);
      const descIdx = headers.indexOf(columnMapping.description);
      const amountIdx = columnMapping.amount ? headers.indexOf(columnMapping.amount) : -1;
      const debitIdx = columnMapping.debit ? headers.indexOf(columnMapping.debit) : -1;
      const creditIdx = columnMapping.credit ? headers.indexOf(columnMapping.credit) : -1;
      const hasDebitCredit = debitIdx !== -1 && creditIdx !== -1;

      if (dateIdx === -1 || descIdx === -1 || (amountIdx === -1 && !hasDebitCredit)) {
        setError(
          'Please map all required columns (Date, Description, and Amount or Debit/Credit).'
        );
        setStep('mapping');
        setIsProcessing(false);
        return;
      }

      // Detect card member column index (optional)
      const cardMemberIdx = columnMapping.cardMember
        ? headers.indexOf(columnMapping.cardMember)
        : -1;

      // Step 1: Parse rows into structured data
      const parsed: FxImportReviewRow[] = parsedRows
        .filter((row) => {
          if (!row[descIdx]?.trim()) return false;
          if (hasDebitCredit) return row[debitIdx]?.trim() || row[creditIdx]?.trim();
          return row[amountIdx]?.trim();
        })
        .filter((row) => {
          // Skip payment/credit rows
          const desc = row[descIdx]?.trim() ?? '';
          return !isPaymentRow(desc);
        })
        .map((row, i) => {
          let rawAmount: number;
          let type: 'income' | 'expense';

          if (hasDebitCredit) {
            const debitVal = parseFloat(row[debitIdx]?.replace(/[,$]/g, '') || '0');
            const creditVal = parseFloat(row[creditIdx]?.replace(/[,$]/g, '') || '0');
            if (invertSign) {
              // Inverted: debit = income, credit = expense
              if (debitVal > 0) {
                rawAmount = debitVal;
                type = 'income';
              } else {
                rawAmount = creditVal;
                type = 'expense';
              }
            } else if (creditVal > 0) {
              rawAmount = creditVal;
              type = 'income';
            } else {
              rawAmount = debitVal;
              type = 'expense';
            }
          } else {
            rawAmount = parseFloat(row[amountIdx]?.replace(/[,$]/g, '') ?? '0');
            const isNegative = rawAmount < 0;
            if (invertSign) {
              type = isNegative ? 'expense' : 'income';
            } else {
              type = isNegative ? 'income' : 'expense';
            }
            rawAmount = Math.abs(rawAmount);
          }

          const rawDesc = row[descIdx]?.trim() ?? '';
          const cleaned = cleanDescription(rawDesc);
          const cardMember = cardMemberIdx !== -1 ? row[cardMemberIdx]?.trim() || null : null;

          return {
            tempId: `row-${i}`,
            date: row[dateIdx]?.trim() ?? '',
            description: cleaned,
            originalDescription: rawDesc,
            amount: rawAmount,
            type,
            cardMember,
            vendorId: null,
            categoryId: null,
            confidence: null,
            matchSource: null,
            aiReasoning: null,
            newVendorName: null,
            accepted: false,
            subscriptionId: null,
            subscriptionName: null,
            isDuplicate: false,
            duplicateOfId: null,
            matchedRuleId: null,
            wasOverridden: false,
            originalVendorId: null,
            originalCategoryId: null,
          };
        });

      // Phase 2: Duplicate detection
      if (parsed.length > 0) {
        const dates = parsed
          .map((r) => r.date)
          .filter(Boolean)
          .sort();
        const minDate = dates[0];
        const maxDate = dates[dates.length - 1];

        if (minDate && maxDate) {
          try {
            const existingResp = await financeTransactionsService.getByDateRange(minDate, maxDate);
            const existing = (existingResp.data as any) ?? [];
            const existingKeys = new Map<string, string>();
            for (const tx of existing) {
              existingKeys.set(duplicateKey(tx.date, tx.description, tx.amount), tx.id);
            }

            for (const row of parsed) {
              const key = duplicateKey(row.date, row.description, row.amount);
              const dupId = existingKeys.get(key);
              if (dupId) {
                row.isDuplicate = true;
                row.duplicateOfId = dupId;
                row.accepted = false;
              }
            }
          } catch {
            // If duplicate check fails, continue without it
          }
        }
      }

      // Step 2: Apply rules (skip duplicates)
      const transferCategoryIds = new Set(
        categories.filter((c: FxCategory) => c.type === 'transfer').map((c: FxCategory) => c.id)
      );
      const unmatched: FxImportReviewRow[] = [];
      const matched: FxImportReviewRow[] = [];

      for (const row of parsed) {
        if (row.isDuplicate) {
          matched.push(row);
          continue;
        }
        const ruleMatch = applyRules(row.description, rules, transferCategoryIds);
        if (ruleMatch) {
          matched.push({
            ...row,
            vendorId: ruleMatch.vendorId,
            categoryId: ruleMatch.categoryId,
            type: ruleMatch.isTransfer ? ('transfer' as any) : row.type,
            confidence: 1.0,
            matchSource: 'rule',
            matchedRuleId: ruleMatch.ruleId,
            accepted: true,
          });
          // Increment hit count in background
          financeRulesService.incrementHitCount(ruleMatch.ruleId).catch(() => {});
        } else {
          unmatched.push(row);
        }
      }

      // Step 3: AI categorization for unmatched (batch of 50)
      let aiMatched: FxImportReviewRow[] = [];
      if (unmatched.length > 0) {
        const batches: FxImportReviewRow[][] = [];
        for (let i = 0; i < unmatched.length; i += 50) {
          batches.push(unmatched.slice(i, i + 50));
        }

        for (const batch of batches) {
          try {
            const aiResult = await financeAiService.categorizeTransactions({
              transactions: batch.map((r) => ({
                tempId: r.tempId,
                date: r.date,
                description: r.description,
                amount: r.amount,
                type: r.type,
              })),
              vendors: vendors.map((v: FxVendor) => ({
                id: v.id,
                name: v.name,
                displayName: v.displayName,
              })),
              categories: categories.map((c: FxCategory) => ({
                id: c.id,
                name: c.name,
                type: c.type,
              })),
              sourceAccount,
            });

            const resultsMap = new Map((aiResult.results ?? []).map((r) => [r.tempId, r]));

            for (const row of batch) {
              const ai = resultsMap.get(row.tempId);
              if (ai) {
                const threshold = getAutoAcceptThreshold(ai.vendorId);
                aiMatched.push({
                  ...row,
                  vendorId: ai.vendorId,
                  categoryId: ai.categoryId,
                  confidence: ai.confidence,
                  matchSource: 'ai',
                  aiReasoning: ai.reasoning,
                  newVendorName: ai.newVendorName,
                  accepted: ai.confidence >= threshold,
                });
              } else {
                aiMatched.push({
                  ...row,
                  matchSource: null,
                  confidence: 0,
                  accepted: false,
                });
              }
            }
          } catch (aiError) {
            // If AI fails, keep rows as unmatched
            aiMatched = [
              ...aiMatched,
              ...batch.map((r) => ({
                ...r,
                matchSource: null as any,
                confidence: 0,
                accepted: false,
              })),
            ];
          }
        }
      }

      // Step 4: Auto-match subscriptions by vendor + amount
      const allProcessed = [...matched, ...aiMatched];
      const matchedVendorIds = [
        ...new Set(
          allProcessed
            .filter((r) => r.vendorId && r.vendorId !== '__new__' && !r.isDuplicate)
            .map((r) => r.vendorId as string)
        ),
      ];

      if (matchedVendorIds.length > 0) {
        try {
          const subsResp = await financeSubscriptionsService.getActiveByVendorIds(matchedVendorIds);
          const activeSubs = ((subsResp.data as any) ?? []) as FxSubscription[];

          if (activeSubs.length > 0) {
            // Build lookup: vendorId → subscriptions
            const subsByVendor = new Map<string, FxSubscription[]>();
            for (const sub of activeSubs) {
              const existing = subsByVendor.get(sub.vendorId) ?? [];
              existing.push(sub);
              subsByVendor.set(sub.vendorId, existing);
            }

            for (const row of allProcessed) {
              if (!row.vendorId || row.vendorId === '__new__' || row.isDuplicate) continue;
              const vendorSubs = subsByVendor.get(row.vendorId);
              if (!vendorSubs?.length) continue;

              // Try exact amount match first
              let match = vendorSubs.find((s) => s.amount === row.amount);

              // If no exact match, try close match (within 5% — handles taxes/fees)
              if (!match) {
                match = vendorSubs.find((s) => {
                  if (s.amount === 0) return false;
                  const diff = Math.abs(s.amount - row.amount);
                  return diff / s.amount <= 0.05;
                });
              }

              if (match) {
                row.subscriptionId = match.id;
                row.subscriptionName = match.name;
              }
            }
          }
        } catch {
          // Subscription matching is best-effort
        }
      }

      // Combine all rows, sorted: duplicates last, then rule-matched first, then AI high confidence, then low/unmatched
      const allRows = allProcessed.sort((a, b) => {
        // Duplicates always at the end
        if (a.isDuplicate !== b.isDuplicate) return a.isDuplicate ? 1 : -1;
        const order = { rule: 0, ai: 1, manual: 2, null: 3 };
        const aOrder = order[a.matchSource as keyof typeof order] ?? 3;
        const bOrder = order[b.matchSource as keyof typeof order] ?? 3;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (b.confidence ?? 0) - (a.confidence ?? 0);
      });

      setReviewRows(allRows);
      setStep('review');
    } catch (err: any) {
      setError(err.message || 'Processing failed');
      setStep('mapping');
    } finally {
      setIsProcessing(false);
    }
  }, [
    headers,
    parsedRows,
    columnMapping,
    rules,
    vendors,
    categories,
    getAutoAcceptThreshold,
    sourceAccount,
    invertSign,
  ]);

  // Trigger auto-processing when state is ready
  // Using a ref-based approach to avoid infinite loops
  const [autoProcessTriggered, setAutoProcessTriggered] = useState(false);

  useEffect(() => {
    if (autoMapped && step === 'processing' && !autoProcessTriggered) {
      setAutoProcessTriggered(true);
      handleStartProcessing();
    }
  }, [autoMapped, step, autoProcessTriggered, handleStartProcessing]);

  const handleUpdateRow = useCallback((tempId: string, updates: Partial<FxImportReviewRow>) => {
    setReviewRows((prev) =>
      prev.map((row) => {
        if (row.tempId !== tempId) return row;

        const newRow = { ...row, ...updates };

        // Phase 4A: Track corrections
        if (
          updates.vendorId !== undefined &&
          updates.vendorId !== row.vendorId &&
          !row.wasOverridden
        ) {
          newRow.wasOverridden = true;
          newRow.originalVendorId = row.vendorId;
          newRow.originalCategoryId = row.categoryId;
        }
        if (
          updates.categoryId !== undefined &&
          updates.categoryId !== row.categoryId &&
          !row.wasOverridden
        ) {
          newRow.wasOverridden = true;
          newRow.originalVendorId = row.vendorId;
          newRow.originalCategoryId = row.categoryId;
        }

        return newRow;
      })
    );
  }, []);

  const handleAcceptAll = useCallback(() => {
    setReviewRows((prev) =>
      prev.map((row) => (row.isDuplicate ? row : { ...row, accepted: true }))
    );
  }, []);

  const handleGoToMapping = useCallback(() => {
    setAutoMapped(false);
    setAutoProcessTriggered(false);
    setStep('mapping');
  }, []);

  const handleConfirmImport = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      const accepted = reviewRows.filter((r) => r.accepted && !r.isDuplicate);
      if (accepted.length === 0) {
        setError('No transactions to import.');
        setIsSaving(false);
        return;
      }

      // Step 1: Create new vendors for rows with "__new__" vendorId
      // Deduplicate by newVendorName (case-insensitive)
      const newVendorRows = accepted.filter((r) => r.vendorId === '__new__' && r.newVendorName);
      const uniqueNewVendorNames = [
        ...new Set(newVendorRows.map((r) => (r.newVendorName ?? '').trim().toLowerCase())),
      ];

      const newVendorMap = new Map<string, string>(); // lowercase name -> created vendor ID

      for (const nameLower of uniqueNewVendorNames) {
        // Check if vendor already exists (case-insensitive match)
        const existingVendor = vendors.find(
          (v: FxVendor) =>
            v.name.toLowerCase() === nameLower || v.displayName?.toLowerCase() === nameLower
        );

        if (existingVendor) {
          newVendorMap.set(nameLower, existingVendor.id);
        } else {
          // Find the original casing from the first matching row
          const matchingRow = newVendorRows.find(
            (r) => (r.newVendorName ?? '').trim().toLowerCase() === nameLower
          );
          const originalName = (matchingRow?.newVendorName ?? '').trim();

          const created = await financeVendorsService.create({ name: originalName });
          newVendorMap.set(nameLower, (created.data as any).id);
        }
      }

      // Step 2: Resolve all vendorIds — replace "__new__" with created IDs, null out invalid ones
      const resolvedRows = accepted.map((r) => {
        let { vendorId } = r;
        if (vendorId === '__new__' && r.newVendorName) {
          vendorId = newVendorMap.get(r.newVendorName.trim().toLowerCase()) ?? null;
        } else if (vendorId === '__new__') {
          vendorId = null;
        }
        return { ...r, vendorId };
      });

      // Create import record
      const importRecord = await financeImportsService.create({
        fileName,
        rowCount: resolvedRows.length,
        sourceAccount: sourceAccount || undefined,
      });
      const importId = (importRecord.data as any).id;

      // Bulk insert transactions
      const createdTxs = await financeTransactionsService.bulkCreate(
        importId,
        resolvedRows.map((r) => ({
          date: r.date,
          description: r.description,
          amount: r.amount,
          type: r.type,
          vendorId: r.vendorId,
          categoryId: r.categoryId,
          subscriptionId: r.subscriptionId,
          aiConfidence: r.confidence,
          aiSuggested: r.matchSource === 'ai',
          userVerified: true,
          matchSource: r.matchSource,
          matchedRuleId: r.matchedRuleId,
          notes: r.cardMember ? `Card member: ${r.cardMember}` : undefined,
        }))
      );

      // Log categorization events for imported transactions (fire-and-forget)
      const createdTxData = (createdTxs.data as any) ?? [];
      if (createdTxData.length > 0) {
        const events = resolvedRows
          .map((r, i) => {
            const txId = createdTxData[i]?.id;
            if (!txId) return null;
            return {
              transactionId: txId,
              eventType: r.wasOverridden
                ? ('import_overridden' as const)
                : ('import_accepted' as const),
              prevVendorId: r.wasOverridden ? r.originalVendorId : null,
              prevCategoryId: r.wasOverridden ? r.originalCategoryId : null,
              prevMatchSource: r.matchSource,
              newVendorId: r.vendorId,
              newCategoryId: r.categoryId,
              ruleId: r.matchedRuleId,
              aiConfidence: r.confidence,
              description: r.description,
              amount: r.amount,
            };
          })
          .filter(Boolean) as Array<
          Parameters<typeof financeCategorizationEventsService.bulkLogEvents>[0][0]
        >;

        financeCategorizationEventsService.bulkLogEvents(events).catch(() => {});
      }

      // Update import status
      const ruleMatched = accepted.filter((r) => r.matchSource === 'rule').length;
      const aiMatchedCount = accepted.filter((r) => r.matchSource === 'ai').length;
      await financeImportsService.update(importId, {
        status: 'completed',
        matchedCount: ruleMatched,
        aiMatchedCount,
        unmatchedCount: accepted.filter((r) => !r.matchSource).length,
      });

      queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTIONS);
      queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTION_IMPORTS);
      setStep('done');

      // Phase 4B+4C: AI Learning — fire-and-forget after import completes
      const overriddenRows = reviewRows.filter((r) => r.wasOverridden && r.accepted);
      const allAcceptedRows = resolvedRows;

      if (overriddenRows.length > 0 || allAcceptedRows.length > 0) {
        setIsLearning(true);
        (async () => {
          try {
            // Build corrections list from overridden rows
            const corrections = overriddenRows.map((r) => {
              const assignedVendor =
                r.vendorId === '__new__'
                  ? r.newVendorName ?? 'Unknown'
                  : vendors.find((v: FxVendor) => v.id === r.vendorId)?.name ?? 'Unknown';
              const assignedCategory =
                categories.find((c: FxCategory) => c.id === r.categoryId)?.name ?? 'Unknown';
              const originalVendor = r.originalVendorId
                ? vendors.find((v: FxVendor) => v.id === r.originalVendorId)?.name ?? null
                : r.newVendorName ?? null;
              return {
                description: r.description,
                assignedVendorName: assignedVendor,
                assignedCategoryName: assignedCategory,
                originalSuggestion: originalVendor,
              };
            });

            // Build accepted rows list
            const acceptedForLearning = allAcceptedRows
              .filter((r) => !r.wasOverridden)
              .slice(0, 100) // Limit to avoid huge payloads
              .map((r) => ({
                description: r.description,
                vendorName:
                  r.vendorId === '__new__'
                    ? r.newVendorName ?? 'Unknown'
                    : vendors.find((v: FxVendor) => v.id === r.vendorId)?.name ?? 'Unknown',
                categoryName:
                  categories.find((c: FxCategory) => c.id === r.categoryId)?.name ?? 'Unknown',
                matchSource: r.matchSource,
              }));

            // Existing rules for conflict detection
            const existingRulesForAi = rules.map((r) => ({
              matchPattern: r.matchPattern,
              matchType: r.matchType,
              vendorName: r.vendor?.name ?? 'Unknown',
            }));

            const learnResult = await financeAiService.learnFromImport({
              corrections,
              acceptedRows: acceptedForLearning,
              existingRules: existingRulesForAi,
            });

            // Create learned rules
            for (const learnedRule of learnResult.rules ?? []) {
              // Check for duplicate rules
              try {
                const existing = await financeRulesService.findByPattern(learnedRule.matchPattern);
                if ((existing.data as any)?.length > 0) continue;
              } catch {
                // Continue if check fails
              }

              // Resolve vendor ID
              let vendorId: string | undefined;
              if (learnedRule.isNewVendor) {
                try {
                  const created = await financeVendorsService.create({
                    name: learnedRule.vendorName,
                  });
                  vendorId = (created.data as any).id;
                } catch {
                  // Vendor might already exist
                  const existing = vendors.find(
                    (v: FxVendor) => v.name.toLowerCase() === learnedRule.vendorName.toLowerCase()
                  );
                  vendorId = existing?.id;
                }
              } else {
                const existing = vendors.find(
                  (v: FxVendor) =>
                    v.name.toLowerCase() === learnedRule.vendorName.toLowerCase() ||
                    v.displayName?.toLowerCase() === learnedRule.vendorName.toLowerCase()
                );
                vendorId = existing?.id;
              }

              // Resolve category ID
              const categoryMatch = categories.find(
                (c: FxCategory) => c.name.toLowerCase() === learnedRule.categoryName.toLowerCase()
              );

              await financeRulesService.create({
                name: `AI: ${learnedRule.vendorName} - ${learnedRule.matchPattern}`,
                matchType: learnedRule.matchType,
                matchPattern: learnedRule.matchPattern,
                vendorId,
                categoryId: categoryMatch?.id,
                source: 'ai_learned',
                priority: 50,
              });
            }

            // Process vendor aliases — create additional contains rules
            for (const aliasGroup of learnResult.vendorAliases ?? []) {
              const vendor = vendors.find(
                (v: FxVendor) => v.name.toLowerCase() === aliasGroup.vendorName.toLowerCase()
              );
              if (!vendor) continue;

              for (const alias of aliasGroup.aliases) {
                try {
                  const existing = await financeRulesService.findByPattern(alias.toLowerCase());
                  if ((existing.data as any)?.length > 0) continue;
                } catch {
                  continue;
                }

                await financeRulesService.create({
                  name: `AI alias: ${alias} → ${vendor.name}`,
                  matchType: 'contains',
                  matchPattern: alias.toLowerCase(),
                  vendorId: vendor.id,
                  categoryId: vendor.categoryId ?? undefined,
                  source: 'ai_learned',
                  priority: 40,
                });
              }
            }

            // Invalidate rules cache so next import picks up learned rules
            queryClient.invalidateQueries('fx-active-rules');
          } catch {
            // AI learning is fire-and-forget — don't surface errors
          } finally {
            setIsLearning(false);
            // Trigger feedback-based learning after import learning completes
            learnInBackground();
          }
        })();
      } else {
        // Even without corrections, trigger feedback learning from accumulated events
        learnInBackground();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save transactions');
    } finally {
      setIsSaving(false);
    }
  }, [
    reviewRows,
    fileName,
    sourceAccount,
    queryClient,
    vendors,
    categories,
    rules,
    learnInBackground,
  ]);

  const handleReset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setHeaders([]);
    setColumnMapping({
      date: '',
      description: '',
      amount: '',
      debit: '',
      credit: '',
      cardMember: '',
    });
    setReviewRows([]);
    setFileName('');
    setSourceAccount('');
    setError(null);
    setAutoMapped(false);
    setAutoProcessTriggered(false);
    setIsLearning(false);
    setInvertSign(false);
  }, []);

  return (
    <FinanceXImportComponent
      step={step}
      headers={headers}
      parsedRows={parsedRows}
      columnMapping={columnMapping}
      reviewRows={reviewRows}
      vendors={vendors}
      categories={categories}
      fileName={fileName}
      sourceAccount={sourceAccount}
      bankAccounts={bankAccounts}
      onSourceAccountChange={setSourceAccount}
      invertSign={invertSign}
      onInvertSignChange={setInvertSign}
      isSaving={isSaving}
      isLearning={isLearning}
      error={error}
      autoMapped={autoMapped}
      onFileUpload={handleFileUpload}
      onColumnMappingChange={setColumnMapping}
      onStartProcessing={handleStartProcessing}
      onUpdateRow={handleUpdateRow}
      onAcceptAll={handleAcceptAll}
      onConfirmImport={handleConfirmImport}
      onReset={handleReset}
      onGoToMapping={handleGoToMapping}
    />
  );
};

export default FinanceXImportContainer;
